// BLV cinematic opening sequence — refinement pass.
// black → avatar fragments + discipline flashes → acceleration → convergence →
// black cut → title assembly + light sweep → crown line-draw → taglines → Founding Cohort reveal.
// Skippable, remembers returning visitors (short logo reveal), respects prefers-reduced-motion.
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CREATORS, MODELS } from "@/data/characters";

const SEEN_KEY = "blv_intro_seen";
const ORDER = ["black", "frag", "converge", "title", "crown", "tags", "final"];

// Fragment cast — close-ups via object-position + zoom inside masked frames.
// from = entry offset, to = settled offset around center. Order matches discipline flashes.
const FRAGS = [
  { img: CREATORS[0].img, label: "PHOTOGRAPHY", pos: "50% 12%", zoom: 1.8, from: { x: "-58vw", y: "-12vh" }, to: { x: "-27vw", y: "-16vh" }, r: -6, cls: "h-40 w-28 sm:h-44 sm:w-64", mobile: true },
  { img: MODELS[1].img, label: "FILM & VIDEO", pos: "50% 8%", zoom: 2.0, from: { x: "58vw", y: "-20vh" }, to: { x: "28vw", y: "-18vh" }, r: 5, cls: "h-44 w-30 sm:h-64 sm:w-52", mobile: true },
  { img: CREATORS[2].img, label: "BEAUTY PRODUCTION", pos: "52% 26%", zoom: 2.2, from: { x: "-56vw", y: "26vh" }, to: { x: "-26vw", y: "20vh" }, r: 4, cls: "h-36 w-32 sm:h-48 sm:w-60", mobile: true },
  { img: CREATORS[6].img, label: "FASHION & STYLING", pos: "50% 10%", zoom: 1.9, from: { x: "54vw", y: "30vh" }, to: { x: "26vw", y: "20vh" }, r: -5, cls: "h-40 w-28 sm:h-56 sm:w-48", mobile: false },
  { img: CREATORS[5].img, label: "WRITING & JOURNALISM", pos: "55% 40%", zoom: 2.3, from: { x: "-30vw", y: "-48vh" }, to: { x: "-11vw", y: "-26vh" }, r: 6, cls: "h-28 w-36 sm:h-32 sm:w-64", mobile: false },
  { img: CREATORS[4].img, label: "MARKETING", pos: "50% 14%", zoom: 1.8, from: { x: "28vw", y: "-50vh" }, to: { x: "12vw", y: "-27vh" }, r: -4, cls: "h-36 w-28 sm:h-44 sm:w-52", mobile: true },
  { img: CREATORS[3].img, label: "CREATIVE DIRECTION", pos: "50% 12%", zoom: 1.8, from: { x: "-24vw", y: "52vh" }, to: { x: "-9vw", y: "27vh" }, r: -6, cls: "h-32 w-32 sm:h-44 sm:w-56", mobile: false },
  { img: MODELS[6].img, label: "MODELS / TALENT", pos: "50% 8%", zoom: 1.9, from: { x: "26vw", y: "54vh" }, to: { x: "11vw", y: "27vh" }, r: 5, cls: "h-40 w-28 sm:h-52 sm:w-44", mobile: true },
];

// Accelerating stagger — gaps shrink as movement builds.
const DELAYS = [0, 0.55, 1.0, 1.4, 1.75, 2.05, 2.3, 2.5];

const Frag = ({ p, delay, converging, isMobile }) => (
  <div className="absolute left-1/2 top-1/2" style={{ transform: "translate(-50%, -50%)" }}>
    <motion.div
      initial={{ opacity: 0, x: p.from.x, y: p.from.y, rotate: p.r * 2, scale: 1.12, clipPath: "inset(0% 100% 0% 0%)", filter: isMobile ? "none" : "blur(5px)" }}
      animate={
        converging
          ? { opacity: [1, 1, 0], x: "0vw", y: "0vh", rotate: 0, scale: 0.22, clipPath: "inset(0% 0% 0% 0%)", filter: isMobile ? "none" : "blur(7px)", transition: { duration: 0.6, times: [0, 0.75, 1], ease: [0.7, 0, 0.84, 0] } }
          : { opacity: 1, x: p.to.x, y: p.to.y, rotate: p.r, scale: 1, clipPath: "inset(0% 0% 0% 0%)", filter: "blur(0px)", transition: { delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] } }
      }
      className={`${p.cls} overflow-hidden border border-gold/50 bg-charcoal shadow-[0_20px_60px_rgba(0,0,0,0.8)]`}
    >
      <img src={p.img} alt="" className="h-full w-full object-cover" style={{ objectPosition: p.pos, transform: `scale(${p.zoom})`, filter: "grayscale(1) contrast(1.15) brightness(0.85)" }} />
      <span className="absolute bottom-1.5 left-1.5 border border-gold/50 bg-onyx/75 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.2em] text-goldlight sm:text-[9px]">{p.label}</span>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-onyx/70 via-transparent to-onyx/30" />
    </motion.div>
  </div>
);

const TitleWord = ({ children, delay, img, reduced }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={reduced ? { opacity: 0 } : { y: "105%" }}
      animate={reduced ? { opacity: 1 } : { y: 0 }}
      transition={{ duration: reduced ? 0.9 : 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block font-display uppercase leading-[0.85] tracking-tight ${img ? "" : "text-metallic"}`}
      style={img ? {
        backgroundImage: `linear-gradient(rgba(200,200,210,0.25), rgba(20,20,22,0.55)), url(${img})`,
        backgroundSize: "cover",
        backgroundPosition: "center 20%",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
        WebkitTextStroke: "1px rgba(201,162,39,0.55)",
        filter: "drop-shadow(0 6px 24px rgba(0,0,0,0.9))",
      } : undefined}
    >
      {children}
    </motion.span>
  </span>
);

const Crown = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="flex flex-col items-center" data-testid="intro-crown">
    <svg width="60" height="40" viewBox="0 0 72 46" fill="none" aria-hidden="true">
      <motion.path d="M8 34 L8 12 L23 24 L36 4 L49 24 L64 12 L64 34 Z" stroke="#E7C873" strokeWidth="1.5" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, ease: "easeInOut" }} />
      <motion.path d="M10 40 L62 40" stroke="#C9A227" strokeWidth="1.5" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.8, duration: 0.35 }} />
    </svg>
    <motion.p initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.5 }} className="mt-2 font-cinzel text-[9px] uppercase tracking-[0.5em] text-gold sm:text-[11px]">
      A Creative Ecosystem
    </motion.p>
  </motion.div>
);

export const CinematicIntro = ({ onDone }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("black");
  const timers = useRef([]);

  const reduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );
  const isMobile = useMemo(() => typeof window !== "undefined" && window.innerWidth < 640, []);
  const seen = useMemo(() => {
    try { return localStorage.getItem(SEEN_KEY) === "1"; } catch { return false; }
  }, []);

  const after = (p) => ORDER.indexOf(phase) >= ORDER.indexOf(p);
  const schedule = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  useEffect(() => {
    document.body.style.overflow = "hidden";
    if (reduced) {
      // Simplified elegant reveal: crown → title → cohort. Fades only.
      setPhase("title");
      schedule(() => setPhase("crown"), 1200);
      schedule(() => setPhase("final"), 2700);
    } else if (seen) {
      // Returning visitor: short logo reveal.
      setPhase("title");
      schedule(() => setPhase("crown"), 900);
      schedule(() => setPhase("final"), 1900);
    } else {
      schedule(() => setPhase("frag"), 800);
      schedule(() => setPhase("converge"), 4300);
      schedule(() => setPhase("title"), 4950);
      schedule(() => setPhase("crown"), 6150);
      schedule(() => setPhase("tags"), 7150);
      schedule(() => setPhase("final"), 8200);
    }
    return () => {
      document.body.style.overflow = "";
      timers.current.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = (to) => {
    try {
      localStorage.setItem(SEEN_KEY, "1");
      sessionStorage.setItem("blv_intro_session", "1");
    } catch {}
    document.body.style.overflow = "";
    onDone();
    if (to) navigate(to);
  };

  const frags = isMobile ? FRAGS.filter((f) => f.mobile) : FRAGS;
  const fragDelays = isMobile ? DELAYS.slice(0, frags.length).map((d, i) => i * 0.5) : DELAYS;

  return createPortal(
    <motion.div
      exit={{ opacity: 0, transition: { duration: 0.7 } }}
      className="fixed inset-0 z-[90] grain-dark overflow-hidden bg-onyx"
      data-testid="cinematic-intro"
    >
      {/* atmosphere — faint warm gold breathing in the dark */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: phase === "black" ? [0.12, 0.35, 0.2] : 0.16 }}
        transition={{ duration: 1.6 }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-[70vmin] w-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(201,162,39,0.16) 0%, transparent 60%)" }}
      />
      <div className="pointer-events-none absolute inset-0 halftone-gold opacity-[0.05]" />

      {/* flying fragments + discipline flashes */}
      <AnimatePresence>
        {(phase === "frag" || phase === "converge") && (
          <motion.div key="frags" exit={{ opacity: 0, transition: { duration: 0.3 } }} className="absolute inset-0" data-testid="intro-fragments">
            {frags.map((p, i) => (
              <Frag key={p.label} p={p} delay={fragDelays[i]} converging={phase === "converge"} isMobile={isMobile} />
            ))}
            {/* editorial discipline lower-thirds, synced to each fragment */}
            {phase === "frag" && frags.map((p, i) => (
              <motion.p
                key={`label-${p.label}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 1, 0] }}
                transition={{ delay: fragDelays[i] + 0.2, duration: 0.55, times: [0, 0.25, 0.65, 1] }}
                className={`absolute inset-x-0 z-10 ${i % 2 ? "bottom-[16%]" : "bottom-[9%]"} text-center font-cinzel text-[11px] uppercase tracking-[0.45em] text-gold/90 sm:text-sm`}
                style={{ textShadow: "0 2px 12px rgba(0,0,0,0.95), 0 0 4px rgba(0,0,0,0.9)" }}
              >
                <span className="mr-3 font-mono text-[8px] tracking-[0.25em] text-silver/50 sm:text-[10px]">DEPT {String(i + 1).padStart(2, "0")}</span>
                {p.label}
              </motion.p>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* black-frame cut into the title */}
      {after("title") && !seen && !reduced && (
        <motion.div initial={{ opacity: 1 }} animate={{ opacity: 0 }} transition={{ duration: 0.45 }} className="pointer-events-none absolute inset-0 bg-onyx" />
      )}

      {/* title assembly → crown → taglines → founding cohort */}
      {after("title") && (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          {/* crown space is reserved so the title never jumps */}
          <div className="flex h-20 flex-col items-center justify-end sm:h-24">
            {after("crown") && <Crown />}
          </div>

          <motion.div initial={{ scale: reduced ? 1 : 0.94 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }} className="relative mt-3">
            <h1 className="text-[16vw] sm:text-[8.5rem]" data-testid="intro-title">
              <TitleWord delay={0.05} reduced={reduced}>Big</TitleWord>
              <TitleWord delay={0.22} reduced={reduced}>Little</TitleWord>
              <TitleWord delay={0.4} img={MODELS[3].img} reduced={reduced}>Village</TitleWord>
            </h1>
            {/* metallic light sweep + restrained flare across the assembled title */}
            {!reduced && (
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <motion.div
                  initial={{ x: "-130%" }}
                  animate={{ x: "160%" }}
                  transition={{ delay: 1.05, duration: 1.15, ease: "easeInOut" }}
                  className="absolute inset-y-0 w-1/3 -skew-x-12 mix-blend-screen"
                  style={{
                    background: "linear-gradient(90deg, transparent, rgba(231,200,115,0.22), rgba(255,255,255,0.14), transparent)",
                    WebkitMaskImage: "linear-gradient(180deg, transparent, black 25%, black 75%, transparent)",
                    maskImage: "linear-gradient(180deg, transparent, black 25%, black 75%, transparent)",
                  }}
                />
                <motion.div
                  initial={{ x: "-20vw", opacity: 0 }}
                  animate={{ x: "30vw", opacity: [0, 0.5, 0] }}
                  transition={{ delay: 1.1, duration: 1.1, ease: "easeInOut" }}
                  className="absolute top-1/3 h-16 w-16 rounded-full mix-blend-screen"
                  style={{ background: "radial-gradient(circle, rgba(243,223,160,0.7) 0%, transparent 65%)" }}
                />
              </div>
            )}
          </motion.div>

          <div className="mt-6 min-h-[7rem]" aria-live="polite">
            <AnimatePresence mode="wait">
              {phase === "tags" && (
                <motion.p key="tag1" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.5 }} className="font-cinzel text-sm uppercase tracking-[0.35em] text-cream sm:text-lg">
                  Creativity. Culture. Community.
                </motion.p>
              )}
              {phase === "final" && (
                <motion.div key="final" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="intro-final">
                  <p className="font-display text-2xl uppercase tracking-wide text-gold-metallic sm:text-4xl">Founding Cohort 2027</p>
                  <div className="gold-rule mx-auto mt-3 w-48" />
                  <p className="mt-3 font-cinzel text-[10px] uppercase tracking-[0.3em] text-cream/85 sm:text-xs">
                    Applications open September 5, 2026
                  </p>
                  <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.3em] text-silver/50">The Village is open.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {phase === "final" && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.6 }} className="mt-4 flex flex-col items-center gap-3 sm:flex-row">
              <button
                onClick={() => finish()}
                data-testid="intro-enter-btn"
                className="btn-sweep border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx shadow-[0_0_30px_rgba(201,162,39,0.35)] transition-transform duration-200 hover:-translate-y-0.5"
              >
                Enter the Village
              </button>
              <button
                onClick={() => finish("/cohort")}
                data-testid="intro-cohort-btn"
                className="border border-silver/40 bg-transparent px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors duration-200 hover:border-gold hover:text-goldlight"
              >
                Explore the Cohort
              </button>
            </motion.div>
          )}
        </div>
      )}

      {/* skip */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        onClick={() => finish()}
        data-testid="intro-skip-btn"
        className="absolute bottom-5 right-5 border border-silver/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-silver/60 transition-colors hover:border-gold hover:text-goldlight"
      >
        Skip Intro
      </motion.button>
    </motion.div>,
    document.body
  );
};
